// R5.3 — THE CORRECTION DIFF (spec §5 step 5).
//
// "The surface renders `band_changes` as a neutral diff ('Leadership: Limited → Demonstrated' — band labels and
// arrows only, never 'improved'/lamented), with the diff a11y prose `CS-DIFF-A11Y` for screen readers."
//
// TWO THINGS IN THAT SENTENCE ARE IN TENSION, AND THE RATIFIED STRING RESOLVES IT. The arrow form needs glue text
// the copy registry does not contain — a colon, a space, a "→". T-COPY-1's allowed-source list is CLOSED at six,
// none of which is "punctuation the component felt was obviously fine", and the DOM sweep walks TEXT NODES, so a
// bare "→" between two chips is an unregistered rendered string exactly like any other. `CS-DIFF-A11Y` —
// "{domain} changed from {before_band} to {after_band}." — says the same thing, in ratified words, with the two
// band labels and the domain name as its slots and no evaluative word anywhere in it.
//
// SO THE RATIFIED SENTENCE IS THE DIFF, VISIBLY AND AUDIBLY, rather than a screen-reader annotation duplicating an
// unregistered visual form. That is a strictly smaller claim than the spec's illustration, not a larger one: it
// renders less, invents nothing, and drops the double announcement a sighted-and-screen-reader user would
// otherwise get.
//
// **ACCEPTED PROVISIONALLY BY THE R5.3 BUILD REVIEW (ambiguity ruling 3), and the clarification is recorded here
// as that ruling directs:** the sentence form is the SENTENCE-VS-ARROW resolution — the surface renders §5 step
// 5's diff as `CS-DIFF-A11Y`'s ratified sentence in BOTH channels, and renders no arrow form at all, because the
// arrow form's glue text has no registered source. It "makes the smaller, accurate claim and derives both band
// labels from their respective payloads". It remains filed for the owner as a §11 copy question — register a
// diff-arrow form, or ratify this reading — and nothing here presumes which way that goes.
//
// WHERE THE BAND LABELS COME FROM, AND WHY NOT FROM A MAP. `band_changes` carries raw enum ids
// (`{subject, before_band, after_band}`, §1.2a) and no copy ids — it is a projection of the deny-listed
// `RecomputationLog`, not a passed-through engine object. The obvious fix, a client-side `ContentBand → CS-BAND-*`
// map, is the "second opinion about the same band" `DomainBands` already refuses. So both labels are resolved off
// PAYLOADS: the AFTER band's copy id from the new read's own `domains[]` row, and the BEFORE band's from the
// PREVIOUS read's row for that same domain — the id the engine itself stamped on the band the applicant was
// actually looking at a moment ago.
//
// AND IT IS CORROBORATED BEFORE IT RENDERS. A change row only renders when the previous read's stored band for
// that domain EQUALS the `before_band` the server reported. If they disagree, the previous read in hand is not
// the one this correction was computed against (a stale tab, a refetch in between), and the honest response is to
// render nothing for that row rather than to describe a transition we cannot evidence — the same rule the rest of
// this surface applies to evidence it cannot resolve.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import { COPY } from './copy';
import { Copy, LayerHeading, layerHeadingId } from './renderContent';
import { BODY_STYLE, SECTION_STYLE } from './styles';

type Domain = RubricReadPublicV1['domains'][number];

interface ResolvedChange {
  subject: string;
  domainCopyId: Domain['domain_copy_string_id'];
  beforeCopyId: Domain['band_copy_string_id'];
  afterCopyId: Domain['band_copy_string_id'];
}

/**
 * Resolve every band change to registered copy ids, dropping any the two payloads cannot jointly evidence.
 *
 * Exported for its own unit tests: the drop rules are the substance here, and asserting them through the DOM
 * would only prove that nothing rendered, not why.
 */
export function resolveBandChanges(
  previous: RubricReadPublicV1 | null,
  next: RubricReadPublicV1,
): ResolvedChange[] {
  const changes = next.band_changes ?? [];
  if (changes.length === 0 || previous === null) return [];
  const before = new Map(previous.domains.map((d) => [d.domain as string, d]));
  const after = new Map(next.domains.map((d) => [d.domain as string, d]));
  const out: ResolvedChange[] = [];
  for (const ch of changes) {
    const b = before.get(ch.subject);
    const a = after.get(ch.subject);
    if (!b || !a) continue;
    // CORROBORATION, both ends. The previous read must actually have held the band the server calls `before_band`,
    // and the new read must actually hold the band it calls `after_band` — otherwise the pair in hand does not
    // describe this transition and no sentence about it is safe to write.
    if (b.band !== ch.before_band || a.band !== ch.after_band) continue;
    out.push({
      subject: ch.subject,
      domainCopyId: a.domain_copy_string_id,
      beforeCopyId: b.band_copy_string_id,
      afterCopyId: a.band_copy_string_id,
    });
  }
  return out;
}

/**
 * The diff, announced.
 *
 * `role="status"` (a polite live region) rather than an alert: a band moving because the applicant corrected a
 * fact is the expected outcome of what they just did, not an interruption. §4.4 (ii)'s "a user action with a
 * visible cause, never a hidden penalty" is the whole reason this renders at all — including when the band FELL,
 * which is a legitimate, stated consequence of removing evidence and is described in the same neutral words as a
 * rise, by the same single registered sentence.
 */
export function BandChangeDiff({
  previous,
  next,
}: {
  previous: RubricReadPublicV1 | null;
  next: RubricReadPublicV1;
}) {
  const changes = resolveBandChanges(previous, next);
  if (changes.length === 0) return null;
  return (
    <section
      aria-labelledby={layerHeadingId('CS-SECTION-CHANGES')}
      style={SECTION_STYLE}
      data-testid="rubric-band-changes"
      role="status"
    >
      <LayerHeading id="CS-SECTION-CHANGES" />
      <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {changes.map((ch) => (
          <li key={ch.subject} style={BODY_STYLE} data-band-change={ch.subject}>
            <Copy
              id="CS-DIFF-A11Y"
              slots={{
                domain: COPY[ch.domainCopyId],
                before_band: COPY[ch.beforeCopyId],
                after_band: COPY[ch.afterCopyId],
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
